import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  CurrencyDollarIcon,
  ClockIcon,
  TruckIcon,
  ShieldCheckIcon,
  CheckIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
  ArrowUpTrayIcon,
  CheckCircleIcon,
  UserIcon,
  PhoneIcon,
  MapPinIcon,
  IdentificationIcon,
  DocumentArrowUpIcon,
} from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';
import Header from './app/Header';
import Footer from './app/Footer';
import FooterCopyright from './app/FooterCopyright';
import supabase from './utils/supabaseClient';
import {
  isValidEmail,
  isValidPhone,
  isValidCPF,
  isValidPlate,
  isAdult,
  isValidYear,
  maskCPF,
  maskPhone,
  maskPlate,
} from './utils/validators';

const STORAGE_KEY = 'fretz:driver-application:v1';

const STEPS = [
  { id: 1, title: 'Pessoal', icon: UserIcon },
  { id: 2, title: 'Contato', icon: PhoneIcon },
  { id: 3, title: 'Veículo', icon: TruckIcon },
  { id: 4, title: 'Documentos', icon: DocumentArrowUpIcon },
  { id: 5, title: 'Revisão', icon: CheckCircleIcon },
];

const DOCUMENTS = [
  {
    kind: 'cnh',
    field: 'cnhUrl',
    label: 'CNH',
    desc: 'Frente e verso (PDF) ou foto legível.',
    accept: 'image/jpeg,image/png,image/webp,application/pdf',
  },
  {
    kind: 'crlv',
    field: 'crlvUrl',
    label: 'CRLV do veículo',
    desc: 'Documento atualizado do veículo.',
    accept: 'image/jpeg,image/png,image/webp,application/pdf',
  },
  {
    kind: 'selfie',
    field: 'selfieUrl',
    label: 'Selfie segurando a CNH',
    desc: 'Foto sua ao lado da CNH para confirmar identidade.',
    accept: 'image/jpeg,image/png,image/webp',
  },
];

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

const safeEmail = (email) =>
  (email || 'anon').toLowerCase().trim().replace(/[^a-z0-9._@-]/g, '_').slice(0, 80);

const uploadDoc = async (file, kind, email) => {
  if (file.size > MAX_FILE_SIZE) {
    throw new Error('Arquivo maior que 10MB.');
  }
  const ext = (file.name.split('.').pop() || 'bin').toLowerCase().slice(0, 8);
  const path = `applications/${safeEmail(email)}/${kind}.${ext}`;
  const { error } = await supabase.storage
    .from('driver-documents')
    .upload(path, file, { upsert: true, contentType: file.type });
  if (error) throw error;
  return path;
};

// Rotas Fretz Empresas: pacotes leves (até 40×40×40 cm / 8 kg).
const VEHICLE_OPTIONS = [
  { value: '',           label: 'Selecione…' },
  { value: 'hatch',      label: 'Hatch / sedan' },
  { value: 'utilitario', label: 'Utilitário leve' },
  { value: 'fiorino',    label: 'Fiorino / furgão pequeno' },
  { value: 'van',        label: 'Van pequena' },
];

// Categorias de CNH compatíveis com veículos de carga
const CNH_OPTIONS = [
  { value: '',  label: 'Selecione…' },
  { value: 'B', label: 'B (até 3,5 ton e 8 lugares)' },
  { value: 'C', label: 'C (carga acima de 3,5 ton até 6 ton)' },
  { value: 'D', label: 'D (transporte de pessoas)' },
  { value: 'E', label: 'E (caminhão acima de 6 ton + reboque)' },
];

const emptyForm = {
  // Pessoal
  firstName: '', lastName: '', cpf: '', birthDate: '',
  // Contato
  email: '', whatsapp: '', city: 'Goiânia', neighborhood: '',
  // Veículo
  vehicle: '', cnhCategory: '', vehiclePlate: '', vehicleModel: '', vehicleYear: '',
  // Documentos (paths no bucket driver-documents)
  cnhUrl: '', crlvUrl: '', selfieUrl: '',
  // Termos
  agreedToTerms: false,
};

const perks = [
  { icon: CurrencyDollarIcon, title: 'Rotas com volume', desc: 'Circuitos diários, semanais e mensais — não é corrida avulsa.' },
  { icon: ClockIcon, title: 'Agenda de rota', desc: 'Você dirige o circuito Fretz, com regra de 15 minutos na porta.' },
  { icon: TruckIcon, title: 'Pacotes leves', desc: 'Até 40×40×40 cm e 8 kg. Sem mudança, sem caminhão.' },
  { icon: ShieldCheckIcon, title: 'Frota identificada', desc: 'Trabalho nas rotas da marca Fretz, na Grande Goiânia.' },
];

const RegisterDriver = () => {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Hidratar do localStorage
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) setForm({ ...emptyForm, ...JSON.parse(saved) });
    } catch (_) { /* ignore */ }
  }, []);

  // Persistir no localStorage
  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(form)); } catch (_) { /* ignore */ }
  }, [form]);

  const update = (field, value) => {
    setForm((f) => ({ ...f, [field]: value }));
    if (errors[field]) setErrors((e) => ({ ...e, [field]: undefined }));
  };

  const validateStep = (s = step) => {
    const e = {};
    if (s === 1) {
      if (!form.firstName.trim()) e.firstName = 'Informe seu nome';
      if (!form.lastName.trim()) e.lastName = 'Informe seu sobrenome';
      if (!isValidCPF(form.cpf)) e.cpf = 'CPF inválido';
      if (!isAdult(form.birthDate)) e.birthDate = 'É necessário ter 18 anos ou mais';
    }
    if (s === 2) {
      if (!isValidEmail(form.email)) e.email = 'E-mail inválido';
      if (!isValidPhone(form.whatsapp)) e.whatsapp = 'WhatsApp inválido';
      if (!form.city.trim()) e.city = 'Informe a cidade';
      if (!form.neighborhood.trim()) e.neighborhood = 'Informe o bairro';
    }
    if (s === 3) {
      if (!form.vehicle) e.vehicle = 'Selecione o veículo';
      if (!form.cnhCategory) e.cnhCategory = 'Selecione a categoria';
      if (!isValidPlate(form.vehiclePlate)) e.vehiclePlate = 'Placa inválida (ex.: ABC1D23)';
      if (!form.vehicleModel.trim()) e.vehicleModel = 'Informe o modelo';
      if (!isValidYear(form.vehicleYear)) e.vehicleYear = 'Ano inválido';
    }
    if (s === 4) {
      if (!form.cnhUrl) e.cnhUrl = 'Envie a CNH';
      if (!form.crlvUrl) e.crlvUrl = 'Envie o CRLV';
      if (!form.selfieUrl) e.selfieUrl = 'Envie a selfie';
    }
    if (s === 5) {
      if (!form.agreedToTerms) e.agreedToTerms = 'Aceite os termos para continuar';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const goNext = () => {
    if (!validateStep()) {
      toast.error('Corrija os campos destacados.');
      return;
    }
    setStep((s) => Math.min(s + 1, STEPS.length));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const goBack = () => {
    setStep((s) => Math.max(s - 1, 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateStep(5)) return;

    setSubmitting(true);
    const { error } = await supabase.from('entregadores').insert([{
      primeiro_nome:  form.firstName,
      sobrenome:      form.lastName,
      cpf:            form.cpf,
      data_nascimento: form.birthDate || null,
      email:          form.email,
      whatsapp:       form.whatsapp,
      cidade:         form.city,
      bairro:         form.neighborhood,
      tipo_veiculo:   form.vehicle,
      cnh_categoria:  form.cnhCategory,
      veiculo_placa:  form.vehiclePlate,
      veiculo_modelo: form.vehicleModel,
      veiculo_ano:    form.vehicleYear ? parseInt(form.vehicleYear, 10) : null,
      cnh_url:        form.cnhUrl || null,
      crlv_url:       form.crlvUrl || null,
      selfie_url:     form.selfieUrl || null,
      status:         'pendente',
    }]);
    setSubmitting(false);

    if (error) {
      toast.error('Erro ao enviar. Tente novamente.');
      console.error(error);
      return;
    }

    // Registra os aceites legais (prova versionada de consentimento)
    const ua = typeof navigator !== 'undefined' ? navigator.userAgent : null;
    const aceites = ['termos', 'privacidade', 'contrato_intermediacao'].map((doc_tipo) => ({
      email: form.email,
      doc_tipo,
      doc_versao: '2.0-2026-05-01',
      user_agent: ua,
      metadata: { origem: 'cadastro_entregador' },
    }));
    supabase.from('aceites_legais').insert(aceites).then(({ error: aErr }) => {
      if (aErr) console.warn('aceite legal não registrado:', aErr);
    });

    toast.success('Cadastro enviado com sucesso!');
    setSubmitted(true);
    try { localStorage.removeItem(STORAGE_KEY); } catch (_) { /* ignore */ }
  };

  // ───────── Tela de sucesso ─────────
  if (submitted) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <section className="relative overflow-hidden bg-white">
          <div className="absolute inset-0 bg-mesh-light" aria-hidden="true" />
          <div className="relative container mx-auto px-4 lg:px-6 py-20 lg:py-28">
            <div className="max-w-xl mx-auto text-center">
              <div className="h-16 w-16 mx-auto rounded-3xl bg-brand-gradient text-white flex items-center justify-center shadow-glow">
                <CheckCircleIcon className="h-8 w-8" />
              </div>
              <h1 className="mt-6 font-display text-display-lg text-ink-900">
                Cadastro enviado!
              </h1>
              <p className="mt-4 text-lg text-ink-500">
                Recebemos sua inscrição. Vamos analisar seus dados e entrar em contato
                pelo WhatsApp <strong className="text-ink-900">{form.whatsapp}</strong> em até 48h úteis.
              </p>
              <div className="mt-8 card p-6 text-left">
                <p className="font-display font-bold text-ink-900 mb-3">Próximos passos</p>
                <ol className="space-y-3 text-sm text-ink-700">
                  {[
                    'Análise dos dados e documentos pelo nosso time',
                    'Confirmação por WhatsApp em até 48h úteis',
                    'Validação final e ativação da conta',
                    'Integração às rotas Fretz na Grande Goiânia',
                  ].map((p, i) => (
                    <li key={p} className="flex gap-3">
                      <span className="h-6 w-6 rounded-full bg-brand-50 text-brand-700 text-xs font-bold flex items-center justify-center shrink-0">{i + 1}</span>
                      {p}
                    </li>
                  ))}
                </ol>
              </div>
              <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
                <Link to="/entregador/status" className="btn-primary gap-2">
                  Ver status da inscrição
                  <ArrowRightIcon className="h-4 w-4" />
                </Link>
                <Link to="/" className="btn-secondary">Voltar para a home</Link>
              </div>
            </div>
          </div>
        </section>
        <Footer />
        <FooterCopyright />
      </div>
    );
  }

  // ───────── Wizard ─────────
  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero */}
      <section className="relative overflow-hidden bg-white">
        <div className="absolute inset-0 bg-mesh-light" aria-hidden="true" />
        <div className="relative container mx-auto px-4 lg:px-6 pt-12 lg:pt-16 pb-6 max-w-3xl">
          <span className="badge-brand mb-5">Trabalhe nas rotas Fretz</span>
          <h1 className="font-display text-display-lg text-ink-900">
            Dirija os circuitos da frota
          </h1>
          <p className="mt-3 text-lg text-ink-500">
            Pacotes leves, Grande Goiânia, regra de 15 minutos na porta.
            Em 5 etapas você se candidata e nosso time analisa.
          </p>
        </div>
      </section>

      {/* Wizard */}
      <section className="bg-white pb-16 lg:pb-24">
        <div className="container mx-auto px-4 lg:px-6 max-w-3xl">
          {/* Progress */}
          <ol className="flex items-center justify-between mb-8 lg:mb-12">
            {STEPS.map(({ id, title, icon: Icon }, idx) => {
              const done = id < step;
              const active = id === step;
              return (
                <li key={id} className="flex items-center flex-1 last:flex-none">
                  <div className="flex flex-col items-center gap-2">
                    <div
                      className={`h-10 w-10 lg:h-12 lg:w-12 rounded-2xl flex items-center justify-center transition-all
                        ${done ? 'bg-brand-gradient text-white shadow-glow' : ''}
                        ${active ? 'bg-brand-gradient text-white shadow-glow ring-4 ring-brand-100' : ''}
                        ${!done && !active ? 'bg-ink-100 text-ink-400' : ''}`}
                    >
                      {done ? <CheckIcon className="h-5 w-5" /> : <Icon className="h-5 w-5" />}
                    </div>
                    <span className={`text-xs font-semibold hidden sm:block ${active ? 'text-ink-900' : 'text-ink-500'}`}>
                      {title}
                    </span>
                  </div>
                  {idx < STEPS.length - 1 && (
                    <div className={`flex-1 h-0.5 mx-2 lg:mx-4 transition ${id < step ? 'bg-brand-500' : 'bg-ink-100'}`} />
                  )}
                </li>
              );
            })}
          </ol>

          <form onSubmit={handleSubmit} className="card p-6 lg:p-8" noValidate>
            {step === 1 && <StepPessoal form={form} update={update} errors={errors} />}
            {step === 2 && <StepContato form={form} update={update} errors={errors} />}
            {step === 3 && <StepVeiculo form={form} update={update} errors={errors} />}
            {step === 4 && <StepDocumentos form={form} update={update} errors={errors} />}
            {step === 5 && <StepRevisao form={form} update={update} errors={errors} setStep={setStep} />}

            {/* Nav */}
            <div className="mt-8 flex items-center justify-between gap-3">
              {step > 1 ? (
                <button type="button" onClick={goBack} className="btn-secondary gap-2">
                  <ArrowLeftIcon className="h-4 w-4" />
                  Voltar
                </button>
              ) : <span />}

              {step < STEPS.length ? (
                <button type="button" onClick={goNext} className="btn-primary gap-2">
                  Continuar
                  <ArrowRightIcon className="h-4 w-4" />
                </button>
              ) : (
                <button type="submit" disabled={submitting} className="btn-primary gap-2">
                  {submitting ? 'Enviando…' : (
                    <>
                      <CheckCircleIcon className="h-5 w-5" />
                      Enviar cadastro
                    </>
                  )}
                </button>
              )}
            </div>
          </form>

          <p className="mt-6 text-xs text-ink-400 text-center">
            Seus dados ficam salvos automaticamente neste navegador. Você pode sair e voltar depois.
          </p>
        </div>
      </section>

      {/* Perks */}
      <section className="section-tight bg-ink-50">
        <div className="container mx-auto px-4 lg:px-6">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {perks.map(({ icon: Icon, title, desc }) => (
              <div key={title} className="card p-5">
                <div className="h-10 w-10 rounded-xl bg-brand-50 text-brand-700 flex items-center justify-center">
                  <Icon className="h-5 w-5" />
                </div>
                <p className="mt-3 font-semibold text-ink-900">{title}</p>
                <p className="text-sm text-ink-500 mt-1">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
      <FooterCopyright />
    </div>
  );
};

// ───────── Sub-componentes por etapa ─────────

const Field = ({ label, htmlFor, error, children, hint }) => (
  <div>
    <label className="label" htmlFor={htmlFor}>{label}</label>
    {children}
    {hint && !error && <p className="mt-1 text-xs text-ink-400">{hint}</p>}
    {error && <p className="mt-1 text-xs text-red-600 font-medium">{error}</p>}
  </div>
);

const StepHeader = ({ icon: Icon, title, desc }) => (
  <div className="mb-6">
    <div className="flex items-center gap-3 mb-3">
      <div className="h-10 w-10 rounded-xl bg-brand-gradient text-white flex items-center justify-center shadow-glow">
        <Icon className="h-5 w-5" />
      </div>
      <h2 className="font-display text-2xl font-bold text-ink-900">{title}</h2>
    </div>
    <p className="text-ink-500">{desc}</p>
  </div>
);

const StepPessoal = ({ form, update, errors }) => (
  <>
    <StepHeader icon={UserIcon} title="Dados pessoais" desc="Comece com seus dados básicos." />
    <div className="space-y-4">
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Nome" htmlFor="firstName" error={errors.firstName}>
          <input id="firstName" className="input" type="text" value={form.firstName}
            onChange={(e) => update('firstName', e.target.value)} placeholder="Maria" autoComplete="given-name" />
        </Field>
        <Field label="Sobrenome" htmlFor="lastName" error={errors.lastName}>
          <input id="lastName" className="input" type="text" value={form.lastName}
            onChange={(e) => update('lastName', e.target.value)} placeholder="Silva" autoComplete="family-name" />
        </Field>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="CPF" htmlFor="cpf" error={errors.cpf} hint="Apenas para validação interna.">
          <input id="cpf" className="input" type="text" value={form.cpf}
            onChange={(e) => update('cpf', maskCPF(e.target.value))} placeholder="000.000.000-00" inputMode="numeric" />
        </Field>
        <Field label="Data de nascimento" htmlFor="birthDate" error={errors.birthDate} hint="Você precisa ter 18 anos ou mais.">
          <input id="birthDate" className="input" type="date" value={form.birthDate}
            onChange={(e) => update('birthDate', e.target.value)} />
        </Field>
      </div>
    </div>
  </>
);

const StepContato = ({ form, update, errors }) => (
  <>
    <StepHeader icon={PhoneIcon} title="Contato" desc="Como falamos com você?" />
    <div className="space-y-4">
      <Field label="E-mail" htmlFor="email" error={errors.email}>
        <input id="email" className="input" type="email" value={form.email}
          onChange={(e) => update('email', e.target.value)} placeholder="voce@email.com" autoComplete="email" />
      </Field>
      <Field label="WhatsApp" htmlFor="whatsapp" error={errors.whatsapp} hint="Use o número que recebe mensagens.">
        <input id="whatsapp" className="input" type="tel" value={form.whatsapp}
          onChange={(e) => update('whatsapp', maskPhone(e.target.value))} placeholder="(62) 9 0000-0000" autoComplete="tel" />
      </Field>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Cidade" htmlFor="city" error={errors.city}>
          <input id="city" className="input" type="text" value={form.city}
            onChange={(e) => update('city', e.target.value)} placeholder="Goiânia" />
        </Field>
        <Field label="Bairro" htmlFor="neighborhood" error={errors.neighborhood}>
          <input id="neighborhood" className="input" type="text" value={form.neighborhood}
            onChange={(e) => update('neighborhood', e.target.value)} placeholder="Setor Bueno" />
        </Field>
      </div>
    </div>
  </>
);

const StepVeiculo = ({ form, update, errors }) => (
  <>
    <StepHeader icon={TruckIcon} title="Veículo e habilitação" desc="Conte sobre o que você dirige." />
    <div className="space-y-4">
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Tipo de veículo" htmlFor="vehicle" error={errors.vehicle}>
          <select id="vehicle" className="input" value={form.vehicle}
            onChange={(e) => update('vehicle', e.target.value)}>
            {VEHICLE_OPTIONS.map((v) => (
              <option key={v.value + v.label} value={v.value}>{v.label}</option>
            ))}
          </select>
        </Field>
        <Field label="Categoria CNH" htmlFor="cnhCategory" error={errors.cnhCategory}>
          <select id="cnhCategory" className="input" value={form.cnhCategory}
            onChange={(e) => update('cnhCategory', e.target.value)}>
            {CNH_OPTIONS.map((v) => (
              <option key={v.value + v.label} value={v.value}>{v.label}</option>
            ))}
          </select>
        </Field>
      </div>
      <div className="grid sm:grid-cols-3 gap-4">
        <Field label="Placa" htmlFor="vehiclePlate" error={errors.vehiclePlate} hint="Mercosul ou antiga.">
          <input id="vehiclePlate" className="input uppercase" type="text" value={form.vehiclePlate}
            onChange={(e) => update('vehiclePlate', maskPlate(e.target.value))} placeholder="ABC1D23" maxLength={7} />
        </Field>
        <Field label="Modelo" htmlFor="vehicleModel" error={errors.vehicleModel}>
          <input id="vehicleModel" className="input" type="text" value={form.vehicleModel}
            onChange={(e) => update('vehicleModel', e.target.value)} placeholder="Fiat Fiorino" />
        </Field>
        <Field label="Ano" htmlFor="vehicleYear" error={errors.vehicleYear}>
          <input id="vehicleYear" className="input" type="number" value={form.vehicleYear}
            onChange={(e) => update('vehicleYear', e.target.value)} placeholder="2020" min="1990" max={new Date().getFullYear() + 1} />
        </Field>
      </div>
    </div>
  </>
);

const DocUpload = ({ kind, label, desc, accept, email, value, onChange, error }) => {
  const [uploading, setUploading] = useState(false);
  const [localError, setLocalError] = useState(null);
  const inputRef = useRef(null);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLocalError(null);
    setUploading(true);
    try {
      const path = await uploadDoc(file, kind, email);
      onChange(path);
    } catch (err) {
      setLocalError(err.message || 'Falha no upload. Tente novamente.');
      console.error(err);
    }
    setUploading(false);
    if (inputRef.current) inputRef.current.value = '';
  };

  const displayError = localError || error;
  const filename = value ? value.split('/').pop() : null;
  const ready = !!value;

  return (
    <div
      className={`rounded-2xl border p-4 transition
        ${displayError ? 'border-red-300 bg-red-50/40' : ready ? 'border-brand-200 bg-brand-50/30' : 'border-ink-100 bg-white'}`}
    >
      <div className="flex items-start gap-3">
        <div
          className={`h-10 w-10 rounded-xl flex items-center justify-center shrink-0
            ${ready ? 'bg-brand-gradient text-white shadow-glow' : 'bg-ink-100 text-ink-500'}`}
        >
          {ready ? <CheckIcon className="h-5 w-5" /> : <DocumentArrowUpIcon className="h-5 w-5" />}
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-ink-900">{label}</p>
          <p className="text-xs text-ink-500 mt-0.5">{desc}</p>
          {ready && (
            <p className="text-xs text-brand-700 mt-1 truncate">Enviado: {filename}</p>
          )}
          {displayError && (
            <p className="text-xs text-red-600 font-medium mt-1">{displayError}</p>
          )}
        </div>
      </div>

      <div className="mt-3">
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading || !email}
          className="btn-secondary w-full text-sm gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {uploading ? 'Enviando…' : (
            <>
              <ArrowUpTrayIcon className="h-4 w-4" />
              {ready ? 'Substituir arquivo' : 'Selecionar arquivo'}
            </>
          )}
        </button>
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          onChange={handleFile}
          className="hidden"
        />
        {!email && (
          <p className="text-xs text-ink-400 mt-2 text-center">
            Informe seu e-mail na etapa de contato antes de enviar arquivos.
          </p>
        )}
      </div>
    </div>
  );
};

const StepDocumentos = ({ form, update, errors }) => (
  <>
    <StepHeader
      icon={DocumentArrowUpIcon}
      title="Documentos"
      desc="Envie os arquivos para nosso time analisar sua inscrição."
    />
    <div className="space-y-4">
      {DOCUMENTS.map((doc) => (
        <DocUpload
          key={doc.kind}
          kind={doc.kind}
          label={doc.label}
          desc={doc.desc}
          accept={doc.accept}
          email={form.email}
          value={form[doc.field]}
          onChange={(path) => update(doc.field, path)}
          error={errors[doc.field]}
        />
      ))}
      <div className="flex items-start gap-2 text-xs text-ink-500 bg-ink-50 border border-ink-100 rounded-2xl p-4">
        <ShieldCheckIcon className="h-4 w-4 text-brand-600 shrink-0 mt-0.5" />
        <span>
          Seus arquivos ficam armazenados de forma segura e são usados apenas para
          análise da inscrição. Tamanho máximo: 10MB. Formatos aceitos: JPG, PNG, WEBP, PDF.
        </span>
      </div>
    </div>
  </>
);

const StepRevisao = ({ form, update, errors, setStep }) => {
  const sections = [
    {
      step: 1, icon: UserIcon, title: 'Dados pessoais',
      rows: [
        ['Nome', `${form.firstName} ${form.lastName}`.trim()],
        ['CPF', form.cpf],
        ['Nascimento', form.birthDate],
      ],
    },
    {
      step: 2, icon: PhoneIcon, title: 'Contato',
      rows: [
        ['E-mail', form.email],
        ['WhatsApp', form.whatsapp],
        ['Cidade', `${form.neighborhood ? form.neighborhood + ', ' : ''}${form.city}`],
      ],
    },
    {
      step: 3, icon: TruckIcon, title: 'Veículo',
      rows: [
        ['Tipo', VEHICLE_OPTIONS.find((v) => v.value === form.vehicle)?.label || '—'],
        ['CNH', form.cnhCategory ? `Categoria ${form.cnhCategory}` : '—'],
        ['Veículo', `${form.vehicleModel} ${form.vehicleYear}`.trim()],
        ['Placa', form.vehiclePlate],
      ],
    },
    {
      step: 4, icon: DocumentArrowUpIcon, title: 'Documentos',
      rows: DOCUMENTS.map((d) => [d.label, form[d.field] ? '✓ Enviado' : '—']),
    },
  ];

  return (
    <>
      <StepHeader icon={CheckCircleIcon} title="Revise e envie" desc="Confira os dados antes de enviar. Você pode editar qualquer etapa." />
      <div className="space-y-4">
        {sections.map(({ step: s, icon: Icon, title, rows }) => (
          <div key={s} className="rounded-2xl border border-ink-100 p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="h-8 w-8 rounded-lg bg-brand-50 text-brand-700 flex items-center justify-center">
                  <Icon className="h-4 w-4" />
                </div>
                <p className="font-bold text-ink-900">{title}</p>
              </div>
              <button type="button" onClick={() => setStep(s)}
                className="text-sm font-semibold text-brand-700 hover:underline">
                Editar
              </button>
            </div>
            <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
              {rows.map(([k, v]) => (
                <div key={k} className="flex justify-between gap-2 border-b border-ink-100 py-1.5 last:border-0">
                  <dt className="text-ink-500">{k}</dt>
                  <dd className="text-ink-900 font-medium text-right break-words">{v || '—'}</dd>
                </div>
              ))}
            </dl>
          </div>
        ))}

        {/* Termos */}
        <label className="flex items-start gap-3 p-4 rounded-2xl border border-ink-100 bg-ink-50 cursor-pointer hover:border-brand-200 transition">
          <input type="checkbox" checked={form.agreedToTerms}
            onChange={(e) => update('agreedToTerms', e.target.checked)}
            className="mt-0.5 accent-brand-600 h-4 w-4 shrink-0" />
          <span className="text-sm text-ink-700">
            <span className="font-semibold flex items-center gap-1.5">
              <IdentificationIcon className="h-4 w-4 text-brand-600" />
              Aceito os termos
            </span>
            <span className="block text-ink-500 mt-1">
              Concordo com os{' '}
              <Link to="/termos" className="text-brand-700 underline" target="_blank" rel="noopener noreferrer">Termos de Uso</Link>,
              {' '}com a{' '}
              <Link to="/privacidade" className="text-brand-700 underline" target="_blank" rel="noopener noreferrer">Política de Privacidade</Link>
              {' '}e com o{' '}
              <Link to="/contrato-entregador" className="text-brand-700 underline" target="_blank" rel="noopener noreferrer">Contrato de Intermediação de Serviços</Link>
              {' '}da Fretz, declarando atuar como profissional autônomo, e autorizo o contato para análise da inscrição.
            </span>
          </span>
        </label>
        {errors.agreedToTerms && <p className="text-xs text-red-600 font-medium">{errors.agreedToTerms}</p>}

        <div className="flex items-start gap-2 text-xs text-ink-500 bg-accent-50/40 border border-accent-200 rounded-2xl p-4">
          <MapPinIcon className="h-4 w-4 text-accent-500 shrink-0 mt-0.5" />
          <span>
            Após enviar, em até 48h úteis nosso time analisa seus dados e documentos
            e confirma a ativação pelo WhatsApp.
          </span>
        </div>
      </div>
    </>
  );
};

export default RegisterDriver;
